import React, { useState } from "react";
import { useSelector } from "react-redux";
import { Field, Formik as FormikBase } from "formik";
import { Link, Navigate } from "react-router-dom";
import LeftView from "@/features/auth/components/LeftView";
import LoadingButton from "@/components/loaders/LoadingButton";
import { emailMDInputFormik } from "@/helpers/formInputs";
import { validateEmail } from "@/helpers/validations";
import { authService } from "@/features/auth/services/authService";
import UnRegisteredEmailMessage from "./UnRegisteredEmailMessage";
import AlreadyHaveAccountMessage from "./AlreadyHaveAccountMessage";
import EmailLinkSendMessage from "../validateEmailSignupLink/EmailLinkSendMessage";
import useQuery from "@/helpers/hooks/useQuery";
import { isLoggedInSelector } from "@/features/auth/store/authSlice";
import MainSEO from "@/components/seo/MainSEO";
import { HTTP_STATUS } from "@/config/constants";
import { Button } from "@mui/material";
import { SITE_NAME } from "@/config/appConfig";
import { getDashboardPath } from "@/helpers/utils";
import { ROUTES } from "@/helpers/routes/routePaths";

const Formik = FormikBase as unknown as React.ComponentType<any>;

export default function Signup() {
  const { subscribe } = useQuery();

  const isLoggedIn = useSelector(isLoggedInSelector);

  const [emailData, setEmailData] = useState(null);
  const [isAssociated, setIsAssociated] = useState(false);
  const [hasCredential, setHasCredential] = useState(false);
  const [isEmailSend, setIsEmailSend] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const onSubmitForSignup = async (values: { email: string }) => {
    setIsLoading(true);
    try {
      const res = await authService.signUp({ ...values, subscribe });
      if (res.status === HTTP_STATUS.OK) {
        const data = res.data as any;
        setEmailData(data);
        setIsAssociated(data.isLocationAllocated);
        setHasCredential(data.hasCredential);
        setIsEmailSend(data.isEmailSend);
      }
    } catch (err) {
      setEmailData({});
    } finally {
      setIsLoading(false);
    }
  };
  const renderSignupForm = () => {
    return (
      <div className="sign-up-form py-4">
        <h2 className="heading mb-3">
          You're a few clicks away from accessing {SITE_NAME}!
        </h2>
        <h6 className="subheading">
          In order to link you to your brand we need you to enter your brand
          email address
        </h6>
        <Formik
          initialValues={{ email: "" }}
          onSubmit={onSubmitForSignup}
          validate={validateEmail}
        >
          {({ handleSubmit }: any) => {
            return (
              <form onSubmit={handleSubmit} className="mt-1 mt-sm-4 mt-md-5">
                <Field
                  name="email"
                  label="E-Mail"
                  component={emailMDInputFormik}
                  autoComplete="off"
                />

                <div className="row justify-content-center justify-content-sm-between form-group mt-3 mt-sm-5">
                  <div className="col-sm-6 col-xl-4 mb-3 mb-sm-0">
                    <LoadingButton
                      type="submit"
                      className="primary"
                      text="Continue"
                      isLoading={isLoading}
                      fullWidth
                      size="large"
                    />
                  </div>
                  <div className="col-sm-3">
                    <Button
                      component={Link}
                      to={ROUTES.LOGIN}
                      variant="outlined"
                      color="primary"
                      disabled={isLoading}
                      size="large"
                      fullWidth
                    >
                      Back
                    </Button>
                  </div>
                </div>
              </form>
            );
          }}
        </Formik>
      </div>
    );
  };

  const renderScreen = () => {
    if (emailData) {
      if (isAssociated) {
        if (hasCredential) {
          return (
            <AlreadyHaveAccountMessage
              email={emailData?.email}
              subscribe={subscribe}
            />
          );
        } else {
          if (isEmailSend) {
            return (
              <EmailLinkSendMessage
                email={emailData?.email}
                subscribe={subscribe as any}
              />
            );
          } else {
            return (
              <h4 className="heading mx-auto">
                Something went wrong. Please try again!
              </h4>
            );
          }
        }
      } else {
        return <UnRegisteredEmailMessage />;
      }
    } else {
      return renderSignupForm();
    }
  };

  if (isLoggedIn) {
    return <Navigate to={getDashboardPath()} replace />;
  }

  return (
    <div className="row login-page">
      <MainSEO title="Signup" url="signup" />
      <div className="col-lg-6 p-0 d-none d-lg-block">
        <LeftView />
      </div>
      <div className="col-lg-6 d-flex flex-column justify-content-center px-lg-5 py-1 py-sm-5 sign-up">
        {renderScreen()}
      </div>
    </div>
  );
}
